import { useState, useCallback } from 'react';
import {
  SimpleFiltersObjectValues,
  FiltersErrors,
  SimpleFiltersObjectType,
} from '@britishcouncil/react-admin';
import { useLocationFiltersCascade } from 'hooks';
import { LocationFilterType } from 'types';

type ValidateFn<TFilterState> = (values: TFilterState) => FiltersErrors | undefined;

export const useReportFiltersCascade = <
  TFilters extends SimpleFiltersObjectType,
  TFilterState extends LocationFilterType = LocationFilterType,
>(
  initialFilterState: SimpleFiltersObjectValues<TFilters> & TFilterState,
  setFilterState: (filterState: TFilterState) => void,
  validate: ValidateFn<TFilterState>
) => {
  const [errors, setErrors] = useState<FiltersErrors>();

  const cascade = useLocationFiltersCascade<TFilters, TFilterState>(
    initialFilterState,
    setFilterState
  );

  const onFiltersApply = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => {
      const validationErrors = validate(filtersValues);
      if (validationErrors) {
        setErrors(validationErrors);
      } else {
        setErrors(undefined);
        cascade.onFiltersApply(filtersValues);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validate, cascade.onFiltersApply]
  );

  const onFiltersChange = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TFilters> & TFilterState) => {
      cascade.onFiltersChange(filtersValues);

      if (errors) {
        const validationErrors = validate(filtersValues);
        if (!validationErrors) {
          setErrors(undefined);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cascade.onFiltersChange, errors, validate]
  );

  return {
    ...cascade,
    errors,
    setErrors,
    onFiltersApply,
    onFiltersChange,
  };
};
